import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import { Alert } from 'components';
import { ClientGroupSourceFragment, useDeleteGroupSourceMutation } from 'api';
import { UpdateGroupInput } from 'api';
import { useNotify } from 'hooks';
import { useDeleteSource } from 'features/groups/updaters';
import { ID_STUB } from 'features/groups/updaters/useCreateSource';

interface IProps {
  source: ClientGroupSourceFragment;
}

const useStyles = makeStyles(theme => ({
  deleteIcon: {
    transform: 'rotate(45deg)',
    color: theme.palette.grey[500],
  },
}));

const SourceDelete: FC<IProps> = ({ source }) => {
  const notify = useNotify();
  const classes = useStyles();
  const { t } = useTranslation();
  const { values } = useFormikContext<UpdateGroupInput>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { id, source_value } = source;
  const deleteSourceMutationOptions = useDeleteSource(values.id);
  const isCreationInProgress = id == ID_STUB;

  const { mutate } = useDeleteGroupSourceMutation({
    onSuccess: handleSuccess,
    ...deleteSourceMutationOptions,
  });

  function toggleConfirm() {
    setDeleteOpen(!deleteOpen);
  }

  function handleDelete() {
    toggleConfirm();
    mutate({ id });
  }

  function handleSuccess() {
    const message = t('groups:deleteSourceSuccess', {
      value: source_value,
      group: values.title,
    });

    notify.info({ message });
  }

  return (
    <Fragment>
      <IconButton disabled={isCreationInProgress} onClick={() => setDeleteOpen(true)}>
        <AddCircleIcon className={classes.deleteIcon} />
      </IconButton>
      <Alert
        open={deleteOpen}
        onConfirm={handleDelete}
        onCancel={toggleConfirm}
        message={t('groups:deleteSourceAlert')}
        confirm={t('common:delete')}
      />
    </Fragment>
  );
};

export default SourceDelete;
